import { useCallback, useEffect, useMemo, useState } from 'react';
import { CatalogSuggestionSource } from 'fym-shared/src/smartlinks/catalog';
import { CatalogSuggestion } from '../../../redux/api/fymAPI.types';
import { buildProfileClaimSnapshot } from './buildClaimSnapshot';
import { useClaimRoute } from './claimWay/useClaimRoute';

/**
 * The claim modal of the Catalog screen: it opens on a suggestion. Adding a song is the
 * step-by-step page (design catalog-add-song), not a modal.
 *
 * The open suggestion, the way and the picked row live in the address (`useClaimRoute`), so
 * Back closes the dialog and a reload opens it again. The suggestion is held once it is found:
 * a claim takes it off the list, and the dialog must stay open for its next step.
 */
export function useCatalogAddModals(suggestions: CatalogSuggestion[] | undefined) {
  const route = useClaimRoute();
  const [held, setHeld] = useState<CatalogSuggestion>();

  useEffect(() => {
    if (route.claimMaid === undefined) {
      setHeld(undefined);
      return;
    }
    const found = suggestions?.find((suggestion) => suggestion.catalogTrackMaid === route.claimMaid);
    if (found !== undefined) {
      setHeld(found);
    }
  }, [route.claimMaid, suggestions]);

  const suggestion = held?.catalogTrackMaid === route.claimMaid ? held : undefined;

  /**
   * A catalog suggestion is read by its maid on the confirm step. A profile one
   * has no catalog track, so its details travel with it instead.
   */
  const claimSnapshot = useMemo(
    () => (suggestion?.source === CatalogSuggestionSource.profile ? buildProfileClaimSnapshot(suggestion) : undefined),
    [suggestion]
  );

  const { open } = route;
  const onClaimSuggestion = useCallback((picked: CatalogSuggestion) => open(picked.catalogTrackMaid), [open]);

  return {
    isClaimOpen: suggestion !== undefined,
    claimMaid: suggestion?.catalogTrackMaid,
    claimSnapshot,
    suggestion,
    route,
    closeClaim: route.close,
    onClaimSuggestion,
  };
}

export type CatalogAddModalsState = ReturnType<typeof useCatalogAddModals>;
